/**
 * Pure logic behind the My Company Account panels: who may see the section,
 * what the Company Details screen renders, and how an update body is built.
 *
 * Kept free of React and of the event bus so the rules that can silently
 * corrupt company data are testable on their own.
 */
import dayjs from "dayjs";
import { ICountryCodes } from "../../Orchestrator/Types/Interface/ICountryCodes";
import {
    ICorporateInformation,
    ICorporateInvoiceDetails,
    ICorporateCard,
    ICorporateResponse,
    ICorporateUser,
    ICorporateUsersPage,
} from "../../Orchestrator/Types/Interface/ICorporate";
import { IUserResponse } from "../../Orchestrator/Types/Interface/IUser";

/**
 * A customer is corporate when /customers/me returns a corporateId, and is the
 * company admin when they also own the company. Both halves are required: the
 * header can show the entry point on corporateId alone (it has no company
 * loaded yet), but every gated screen must ask this.
 */
export const isCorporateAdmin = (
    user: IUserResponse | null | undefined,
    corporate: ICorporateResponse | null | undefined
): boolean => {
    if (!user || !corporate) return false;
    if (user.corporateId == null) return false;
    if (!corporate.ownerId || !user.userId) return false;
    return corporate.ownerId === user.userId;
};

export interface ICompanyDetailsView {
    companyName: string;
    trn: string;
    /** Declared on Corporate Information in 2.0.5; blank when not on file. */
    mobile: string;
    /** Declared on Corporate Information in 2.0.5; blank when not on file. */
    landline: string;
    /** corporateEmail on Corporate Information; blank when not on file. */
    companyEmail: string;
    address: string;
    city: string;
    countryCode: string;
    contractNumber: string;
    contractExpiry: string;
    /** null when the company has no contract loaded; hides the chip. */
    discount: number | null;
    invoiceDetails: ICorporateInvoiceDetails;
    cards: ICorporateCard[];
}

const text = (value: unknown): string =>
    value === null || value === undefined ? "" : String(value);

/**
 * Flattens a corporate into what the Company Details screen renders. Every
 * field degrades to an empty string so a half-populated company renders blanks
 * instead of throwing.
 */
export const toCompanyDetailsView = (
    corporate: ICorporateResponse | null | undefined
): ICompanyDetailsView | null => {
    if (!corporate) return null;

    const data = corporate.data;
    const address = data?.address;
    const contract = data?.contract;

    return {
        companyName: text(corporate.name),
        trn: text(data?.invoiceDetails?.tnr),
        mobile: text(data?.mobile),
        // Declared on Corporate Information in transcity 2.0.5 ("Landline phone
        // number of the company"). It read as non-existent against the stale
        // 1.6.3 file; blank here just means the company has none on file.
        landline: text(data?.landline),
        companyEmail: text(data?.corporateEmail),
        address: text(address?.street),
        city: text(address?.city),
        countryCode: text(address?.countryCode),
        // businessCustomerId is the agreement reference (backend status report
        // row 9); contractNumber is a different, unrelated Turnit value and is
        // deliberately not used as a fallback anywhere.
        contractNumber: text(data?.businessCustomerId),
        contractExpiry: text(contract?.endDate),
        discount: typeof contract?.discount === "number" ? contract.discount : null,
        invoiceDetails: data?.invoiceDetails ?? {},
        cards: data?.cards ?? [],
    };
};

/**
 * PUT /corporates/{id} takes the whole Corporate Information object and
 * declares no required fields, but the endpoint REPLACES rather than merges —
 * measured on INT 19 Aug 2026 — so any field left out of the body is erased.
 * Every update therefore starts from the complete loaded object.
 *
 * `contract` is required here for that reason, even though the endpoint 403s on
 * its presence. The two facts together mean a corporate admin cannot save at
 * all today: with the contract the request is refused, without it the contract
 * is destroyed. Failing loudly is the only safe option — see row 19 of
 * docs/corporate-backend-requirements.csv. DO NOT "fix" this by dropping the
 * contract: that was tried against company 94 and erased its contract, which
 * could not be restored because restoring requires the same 403ing PUT.
 */
const requireInformation = (
    corporate: ICorporateResponse | null | undefined
): ICorporateInformation => {
    const data = corporate?.data;
    if (!data?.address || !data?.contract) {
        throw new Error(
            "[corporateView] Refusing to build an update body: the loaded corporate " +
            "has no address/contract, and this PUT replaces rather than merges, " +
            "so a partial body would erase them. Reload the company before saving."
        );
    }
    return data;
};

const hasAnyValue = (details: ICorporateInvoiceDetails): boolean =>
    Object.values(details).some((value) => text(value).trim() !== "");

/**
 * Whether the company already has invoice details stored.
 *
 * Drives the Invoice Details opt-in: once saved, the checkbox locks, mirroring
 * the personal account (PersonalInformation.tsx) — the backend needs a name and
 * address on the block, so it cannot simply be un-ticked away.
 */
export const hasSavedInvoiceDetails = (
    corporate: ICorporateResponse | null | undefined
): boolean => {
    const details = corporate?.data?.invoiceDetails;
    return Boolean(details) && hasAnyValue(details!);
};

/**
 * `street` and `city` are required by the contract whenever invoiceDetails is
 * present. Exported so the form can gate Save on the same rule the builder
 * enforces, rather than the two drifting apart.
 */
export const isInvoiceDetailsComplete = (
    details: ICorporateInvoiceDetails
): boolean =>
    text(details.street).trim() !== "" && text(details.city).trim() !== "";

/**
 * Replaces the invoice details, leaving every other part of the company as
 * loaded. An all-blank block is omitted rather than sent: the contract marks
 * invoiceDetails.street and .city as minLength 1, so `{}` is a 400, not a no-op.
 */
export const buildCorporateUpdateBody = (
    corporate: ICorporateResponse | null | undefined,
    invoiceDetails: ICorporateInvoiceDetails
): ICorporateInformation => {
    const data = requireInformation(corporate);
    if (!hasAnyValue(invoiceDetails)) {
        const { invoiceDetails: _dropped, ...rest } = data;
        return { ...rest };
    }
    // Present-but-incomplete is a guaranteed 400: the contract marks street and
    // city minLength 1 whenever the block is sent. Fail here, where the caller
    // can name the fields, rather than at the backend with a generic error.
    if (!isInvoiceDetailsComplete(invoiceDetails)) {
        throw new Error(
            "[corporateView] Invoice details need both a street (Address line 1) " +
            "and a city before they can be saved."
        );
    }
    return { ...data, invoiceDetails: { ...invoiceDetails } };
};

/**
 * Drops one stored card by POSITION, leaving every other part of the company as
 * loaded. Filtering on cardNumber would remove every card sharing that number —
 * this app cannot create a duplicate, but the back office can.
 */
export const buildCardRemovalBody = (
    corporate: ICorporateResponse | null | undefined,
    cardIndex: number
): ICorporateInformation => {
    const data = requireInformation(corporate);
    const cards = data.cards ?? [];
    if (cardIndex < 0 || cardIndex >= cards.length) {
        throw new Error(`[corporateView] No card at index ${cardIndex} to remove.`);
    }
    return { ...data, cards: cards.filter((_, index) => index !== cardIndex) };
};

/**
 * The backend returns contract dates as ISO timestamps; the design shows
 * DD-MM-YYYY. Unparseable input renders empty rather than "Invalid Date".
 */
export const formatContractDate = (value: string | undefined | null): string => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "";
};

/**
 * Addresses carry an ISO alpha-2 code; the design shows the country name. Falls
 * back to the raw code when the country metadata has not loaded, so the field
 * degrades to "AE" rather than going blank.
 */
export const resolveCountryName = (
    countryCode: string | undefined | null,
    countries: ICountryCodes[] | undefined | null
): string => {
    if (!countryCode) return "";
    const match = (countries ?? []).find(
        (country) => country.countryCode === countryCode
    );
    return match?.countryNameEn || match?.label || countryCode;
};

/**
 * `GET /corporates/{id}/users` returns TeamUserPage, whose rows carry the
 * member's name and email nested under `data` — reading them off the row
 * itself yields a list of blanks.
 */
/**
 * @param ownerId `corporate.ownerId` — the authoritative owner. Rank ids are
 * team-defined strings, so matching the literal "owner" is a guess; getting it
 * wrong puts a Delete control on the company owner's row.
 */
export const toCorporateUsers = (
    page: ICorporateUsersPage | null | undefined,
    ownerId?: string
): ICorporateUser[] =>
    (page?.content ?? []).map((row) => {
        const firstName = text(row?.data?.firstName);
        const lastName = text(row?.data?.lastName);
        return {
            id: text(row?.id),
            rank: text(row?.rank),
            firstName,
            lastName,
            fullName:
                text(row?.metadata?.name) || `${firstName} ${lastName}`.trim(),
            email: text(row?.data?.email),
            status: text(row?.data?.status),
            // Two separate decisions here:
            //
            // 1. ownerId vs rank -- NOT combined. When ownerId is present it is
            //    authoritative and the rank literal is ignored, because a team
            //    can name a rank "owner" for several members and treating those
            //    as admins would silently hide the controls for managing them.
            //    The rank literal is only a fallback for a missing ownerId.
            //
            // 2. Within the ownerId branch, both ids the row carries are tried,
            //    because it is undocumented which one holds the customer uuid
            //    that ownerId is. If neither does, no row is the owner and the
            //    owner's own row gets a Delete control -- irreversible, with
            //    Add Employee 403ed. Trying both costs nothing and removes one
            //    route there; the id-space question itself is CSV row 20.
            isAdmin: ownerId
                ? text(row?.id) === ownerId || text(row?.data?.id) === ownerId
                : text(row?.rank).toLowerCase() === "owner",

            phoneNumber: text(row?.data?.phoneNumber),
            // Both are returned by the roster and both are shown back in the
            // edit form, so they must survive the mapping — leaving them out is
            // why Gender and Date of Birth opened blank for members who had
            // them set.
            gender: text(row?.data?.gender),
            birthdate: text(row?.data?.birthdate),
            // Absent attributes means "never granted", not "unknown": the
            // object is omitted entirely for members whose permissions have
            // not been set, so false is the correct reading.
            bookingStandardAllowed: row?.attributes?.bookingStandardAllowed === true,
            bookingPremiumAllowed: row?.attributes?.bookingPremiumAllowed === true,
        };
    });

/** Client-side name/email search, matching the design's "Search Employee Name". */
export const filterEmployees = (
    members: ICorporateUser[] | null | undefined,
    query: string
): ICorporateUser[] => {
    const needle = text(query).trim().toLowerCase();
    if (!needle) return members ?? [];
    return (members ?? []).filter((m) =>
        `${m.fullName} ${m.email}`.toLowerCase().includes(needle)
    );
};
